import { useEffect, useState } from 'react'
import { capitalize } from '@node-space/utils'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { getCustomerAddress } from 'pages/Customers/helpers/utils'
import {
  BusinessCustomerType,
  CustomerEntityType,
  IndividualCustomerType,
  NestedCustomerDetails,
} from 'types/customers'
import { getCountryNameByCode } from 'utils/utils'
import { CustomerDepositDetails, CustomerDetails } from '../types'

const defaultCustomerDetails: CustomerDetails = {
  name: '',
  type: '' as CustomerEntityType,
  details: {},
}

const defaultCustomerDepositDetails: CustomerDepositDetails = {
  name: '',
  address: '',
}

export type GetCustomerInfoMethod<CustomerType> = (customer: CustomerType) => CustomerDetails

export const useCustomerWalletDepositDetails = (customer: NestedCustomerDetails) => {
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>(defaultCustomerDetails)
  const [customerDepositDetails, setCustomerDepositDetails] = useState<CustomerDepositDetails>(
    defaultCustomerDepositDetails
  )

  const { data: countries, isFetching: isFetchingCountries } = useCountriesQuery(true)

  const getCustomerTypeLabel = (type: CustomerEntityType) => {
    return type ? capitalize(type?.toLowerCase()) : ''
  }

  const getBusinessCustomerInfo: GetCustomerInfoMethod<BusinessCustomerType> = businessCustomer => {
    const { company, type, reference, externalReference, riskScore } = businessCustomer || {}
    return {
      name: company?.name ?? '',
      type,
      details: {
        customerType: getCustomerTypeLabel(type),
        registrationNumber: company?.registrationNumber ?? '',
        taxResidency: getCountryNameByCode(company?.taxResidenceCountryCode ?? '', countries),
        businessAddress: getCustomerAddress(company?.address),
        representativeName: `${company?.representative?.firstName ?? ''} ${company?.representative?.lastName ?? ''}`,
        reference: reference ?? '',
        externalReference: externalReference ?? '',
        riskScore: riskScore ?? '',
        industry: company?.industry?.name ?? '',
      },
    }
  }

  const getIndividualCustomerInfo: GetCustomerInfoMethod<
    IndividualCustomerType
  > = individualCustomer => {
    const { individual: { details, person } = {}, type } = individualCustomer || {}
    return {
      name: `${person?.firstName ?? ''} ${person?.lastName ?? ''}`,
      type,
      details: {
        customerType: getCustomerTypeLabel(type),
        personalIdNumber: details?.documentNumber ?? '',
        countryOfNationality: getCountryNameByCode(details?.birthCountryCode ?? '', countries),
        residentialAddress: getCustomerAddress(person?.address),
      },
    }
  }

  const getCustomerInfo: GetCustomerInfoMethod<NestedCustomerDetails> = customerData => {
    if (customerData?.type === CustomerEntityType.COMPANY) {
      return getBusinessCustomerInfo(customerData as BusinessCustomerType)
    }
    return getIndividualCustomerInfo(customerData as IndividualCustomerType)
  }

  useEffect(() => {
    if (customer && countries?.length) {
      const customerInfo = getCustomerInfo(customer)
      const isBusinessCustomer = customer?.type === CustomerEntityType.COMPANY
      const depositDetails = {
        name: customerInfo?.name ?? '',
        address: isBusinessCustomer
          ? customerInfo?.details?.businessAddress
          : customerInfo?.details?.residentialAddress,
      }
      setCustomerDetails(customerInfo)
      setCustomerDepositDetails(depositDetails)
    }
  }, [customer, countries])

  return {
    customerDetails,
    customerDepositDetails,
    isFetchingCountries,
  }
}
